import { useTranslation } from "react-i18next";

import type { LoadApiOperation, LoadApiReference } from "@/api/integration";
import { Card, SectionHead } from "@/components/brand/Card";
import { Pill, type PillTone } from "@/components/brand/Pill";

const METHOD_TONE: Record<string, PillTone> = {
  get: "ok",
  post: "navy",
  put: "gold",
  delete: "danger",
};

const BATCH_ROUTE = /\/tables\/([a-z_]+)\/batches\/\{batch_number\}$/;

/**
 * As rotas na ordem em que quem integra as usa: o manifesto diz o que extrair, a carga abre,
 * recebe fontes e lotes, cada tabela completa, e a carga fecha (ou é abandonada). Rota nova que
 * não esteja aqui vai para o fim.
 */
const FLOW: [string, RegExp][] = [
  ["get", /\/manifest$/],
  ["get", /\/manifest\/schema$/],
  ["post", /\/loads$/],
  ["put", /\/sources\/\{sha256\}$/],
  ["put", BATCH_ROUTE],
  ["post", /\/tables\/\{table\}\/complete$/],
  ["get", /\/loads\/\{load_id\}$/],
  ["post", /\/close$/],
  ["post", /\/abandon$/],
];
const BATCH_RANK = 4;

function flowRank(method: string, path: string): number {
  const rank = FLOW.findIndex(([flowMethod, pattern]) => flowMethod === method && pattern.test(path));
  return rank === -1 ? FLOW.length : rank;
}

type JsonSchema = {
  type?: string;
  $ref?: string;
  enum?: string[];
  anyOf?: JsonSchema[];
  items?: JsonSchema;
  description?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
};

type Route = { method: string; path: string; operation: LoadApiOperation };

const refName = (ref: string) => ref.split("/").pop() ?? ref;

/** O tipo de uma propriedade do esquema, sem o `null` dos opcionais. */
function schemaType(schema: JsonSchema): string {
  const concrete = (schema.anyOf ?? [schema]).filter((option) => option.type !== "null");
  const main = concrete[0] ?? schema;
  if (main.$ref) return refName(main.$ref);
  if (main.type === "array" && main.items) return `array<${schemaType(main.items)}>`;
  if (main.enum) return main.enum.join(" | ");
  return main.type ?? "object";
}

function requestSchema(operation: LoadApiOperation): { name: string; ref?: string } | null {
  const content = (operation.requestBody as { content?: Record<string, { schema?: JsonSchema }> })
    ?.content;
  if (!content) return null;
  const [contentType, body] = Object.entries(content)[0] ?? [];
  if (!contentType) return null;
  const ref = body?.schema?.$ref;
  return ref ? { name: refName(ref), ref } : { name: contentType };
}

/**
 * A referência das rotas de carga. As rotas de lote, uma por tabela, viram uma entrada só: o que
 * muda entre elas é a tabela, e os campos de cada uma estão no dicionário.
 */
export function LoadApiReferenceView({ reference }: { reference: LoadApiReference }) {
  const { t } = useTranslation();
  const routes: Route[] = [];
  const batchTables: string[] = [];
  for (const [path, operations] of Object.entries(reference.paths)) {
    const batch = path.match(BATCH_ROUTE);
    if (batch) {
      batchTables.push(batch[1]);
      continue;
    }
    for (const [method, operation] of Object.entries(operations)) {
      routes.push({ method, path, operation });
    }
  }
  routes.sort((a, b) => flowRank(a.method, a.path) - flowRank(b.method, b.path));
  const beforeBatches = routes.filter((route) => flowRank(route.method, route.path) < BATCH_RANK);
  const afterBatches = routes.filter((route) => flowRank(route.method, route.path) > BATCH_RANK);
  const schemas = reference.components.schemas as Record<string, JsonSchema>;

  return (
    <section>
      <SectionHead
        overline={t("LOAD API")}
        title={t("Routes")}
        tag={`${routes.length + batchTables.length}`}
        sub={t(
          "A load opens, receives one batch per table, and closes; only then it becomes the project's current load. Every route takes the project's API key in the Authorization header (Bearer mgk_…). The routes below come from the API itself.",
        )}
      />
      <Card>
        <ul className="divide-y divide-[#f2f4f7]">
          {beforeBatches.map((route) => (
            <RouteItem key={`${route.method} ${route.path}`} route={route} schemas={schemas} />
          ))}
          {batchTables.length > 0 && (
            <li className="px-4 py-4 flex flex-col gap-2" data-testid="api-route-batches">
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone={METHOD_TONE.put}>PUT</Pill>
                <span className="mono text-[13px] text-[#08254c] break-all">
                  /api/load/v1/projects/{"{project_id}"}/loads/{"{load_id}"}/tables/{"{table}"}/batches/
                  {"{batch_number}"}
                </span>
              </div>
              <p className="text-[13px] text-[#596b85] m-0">
                {t(
                  "One route per table of the dictionary. The body carries the table's records, from 1 to 5,000 per batch, with the fields listed in the dictionary. Batches are numbered from 1.",
                )}
              </p>
              <span className="mono muted text-[11px] break-all">{batchTables.join(" · ")}</span>
            </li>
          )}
          {afterBatches.map((route) => (
            <RouteItem key={`${route.method} ${route.path}`} route={route} schemas={schemas} />
          ))}
        </ul>
      </Card>
    </section>
  );
}

function RouteItem({ route, schemas }: { route: Route; schemas: Record<string, JsonSchema> }) {
  const { t } = useTranslation();
  const { method, path, operation } = route;
  const body = requestSchema(operation);
  const bodySchema = body?.ref ? schemas[body.name] : undefined;
  const parameters = (operation.parameters ?? []).map((parameter) => parameter.name);
  const responses = Object.keys(operation.responses ?? {});

  return (
    <li className="px-4 py-4 flex flex-col gap-2" data-testid={`api-route-${method}-${path}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={METHOD_TONE[method] ?? "muted"}>{method.toUpperCase()}</Pill>
        <span className="mono text-[13px] text-[#08254c] break-all">{path}</span>
      </div>
      {(operation.description || operation.summary) && (
        <DocText text={operation.description || operation.summary || ""} />
      )}
      <div className="flex gap-x-5 gap-y-1 flex-wrap text-[11px] text-[#7b8799]">
        {parameters.length > 0 && (
          <span>
            {t("Parameters")}: <span className="mono">{parameters.join(", ")}</span>
          </span>
        )}
        {responses.length > 0 && (
          <span>
            {t("Responses")}: <span className="mono">{responses.join(", ")}</span>
          </span>
        )}
      </div>
      {body && (
        <details className="text-[12px]">
          <summary className="cursor-pointer text-[#08254c]">
            {t("Request body")}: <span className="mono">{body.name}</span>
          </summary>
          {bodySchema?.properties && (
            <div className="overflow-x-auto mt-2">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: "26%" }}>{t("Field")}</th>
                    <th style={{ width: "24%" }}>{t("Type")}</th>
                    <th style={{ width: "12%" }}>{t("Required")}</th>
                    <th>{t("Description")}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(bodySchema.properties).map(([name, prop]) => (
                    <tr key={name}>
                      <td className="mono text-[12px] text-[#08254c]">{name}</td>
                      <td className="mono text-[11px] text-[#596b85] break-all">{schemaType(prop)}</td>
                      <td>
                        {bodySchema.required?.includes(name) ? (
                          <Pill tone="navy">{t("required")}</Pill>
                        ) : (
                          <span className="muted text-[12px]">{t("optional")}</span>
                        )}
                      </td>
                      <td className="text-[12px] text-[#08254c]">{prop.description ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      )}
    </li>
  );
}

/**
 * A descrição vem da docstring da rota: parágrafo separado por linha em branco, linha quebrada
 * pela largura do código e trecho de código entre crases duplas (reStructuredText).
 */
function DocText({ text }: { text: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      {text.split(/\n\s*\n/).map((paragraph, index) => (
        <p key={index} className="text-[13px] text-[#596b85] m-0">
          {paragraph
            .replace(/\s*\n\s*/g, " ")
            .split(/``([^`]+)``/)
            .map((part, partIndex) =>
              partIndex % 2 === 1 ? (
                <code key={partIndex} className="mono text-[12px] text-[#08254c]">
                  {part}
                </code>
              ) : (
                part
              ),
            )}
        </p>
      ))}
    </div>
  );
}
